/**
 * @CODE:SPEC-ACC-001-API-UTILS
 * Accommodation API Utilities
 *
 * Helper functions and utilities for accommodation API operations:
 * - Query builders and filters
 * - Data transformation
 * - Error handling utilities
 */

import type {
  AccommodationFilters,
  AccommodationWithDetails,
} from "@/features/accommodation/types";

/**
 * Calculate pagination offset from page number
 *
 * @param page - Page number (1-indexed)
 * @param limit - Items per page
 * @returns Offset for database query
 */
export function calculateOffset(page: number, limit: number): number {
  return Math.max(0, (page - 1) * limit);
}

/**
 * Calculate total pages from total count and limit
 *
 * @param total - Total number of items
 * @param limit - Items per page
 * @returns Total number of pages
 */
export function calculateTotalPages(total: number, limit: number): number {
  return Math.max(0, Math.ceil(total / limit));
}

/**
 * Validate pagination parameters
 *
 * @param page - Page number
 * @param limit - Items per page
 * @returns Valid page and limit values
 */
export function validatePagination(
  page: number | undefined,
  limit: number | undefined,
): { page: number; limit: number } {
  const validPage = Math.max(1, page ?? 1);
  const validLimit = Math.max(1, Math.min(100, limit ?? 12)); // Max 100 items per page

  return { page: validPage, limit: validLimit };
}

/**
 * Validate geographic coordinates
 *
 * @param latitude - Latitude (-90 to 90)
 * @param longitude - Longitude (-180 to 180)
 * @returns Boolean indicating if coordinates are valid
 */
export function isValidCoordinate(
  latitude: number,
  longitude: number,
): boolean {
  return (
    latitude >= -90 && latitude <= 90 && longitude >= -180 && longitude <= 180
  );
}

/**
 * Convert radius from kilometers to meters
 *
 * PostGIS ST_DWithin expects distance in meters
 *
 * @param radiusKm - Radius in kilometers
 * @returns Radius in meters
 */
export function convertRadiusKmToMeters(radiusKm: number): number {
  return radiusKm * 1000;
}

/**
 * Build display text for accommodation filters
 *
 * @param filters - Accommodation filters
 * @returns Readable filter description
 */
export function buildFilterDescription(
  filters: AccommodationFilters,
): string[] {
  const descriptions: string[] = [];

  if (filters.province) {
    descriptions.push(`지역: ${filters.province}`);
  }

  if (filters.district) {
    descriptions.push(`${filters.district}`);
  }

  if (filters.accommodation_type) {
    descriptions.push(`숙소 유형: ${filters.accommodation_type}`);
  }

  if (filters.priceFrom && filters.priceTo) {
    descriptions.push(
      `가격: ${filters.priceFrom.toLocaleString()}~${filters.priceTo.toLocaleString()}원`,
    );
  } else if (filters.priceFrom) {
    descriptions.push(`가격: ${filters.priceFrom.toLocaleString()}원 이상`);
  } else if (filters.priceTo) {
    descriptions.push(`가격: ${filters.priceTo.toLocaleString()}원 이하`);
  }

  if (filters.search) {
    descriptions.push(`검색: "${filters.search}"`);
  }

  return descriptions;
}

/**
 * Format accommodation price for display
 *
 * @param price - Price in KRW
 * @returns Formatted price string
 */
export function formatPrice(price: number): string {
  return price.toLocaleString("ko-KR", {
    style: "currency",
    currency: "KRW",
    minimumFractionDigits: 0,
  });
}

/**
 * Get main image URL (without fallback)
 *
 * @param accommodation - Accommodation data
 * @returns Image URL or empty string if no image
 */
export function getMainImageUrl(
  accommodation: AccommodationWithDetails,
): string {
  // Return main_image URL or empty string
  return accommodation.main_image?.image_url || "";
}

/**
 * Build sort field name for database query
 *
 * Maps filter sortBy values to actual database column names
 *
 * @param sortBy - Sort field from filter
 * @returns Database column name
 */
export function mapSortByToColumn(
  sortBy: "name" | "price" | "type" | undefined,
): string {
  switch (sortBy) {
    case "price":
      return "price_from";
    case "type":
      return "accommodation_type.type_name";
    case "name":
    default:
      return "name";
  }
}

/**
 * Normalize accommodation data
 *
 * Ensures all required fields are present and properly formatted
 *
 * @param data - Raw accommodation data
 * @returns Normalized accommodation
 */
export function normalizeAccommodation(
  data: Partial<AccommodationWithDetails>,
): AccommodationWithDetails | null {
  const required = [
    "accommodation_id",
    "name",
    "province",
    "district",
    "address",
    "latitude",
    "longitude",
    "price_from",
    "description",
    "accommodation_type",
    "main_image",
  ];

  // Check if all required fields are present
  if (!required.every((field) => field in data)) {
    return null;
  }

  return data as AccommodationWithDetails;
}
